import {
  Component, OnInit, AfterViewInit, AfterViewChecked, OnDestroy,
  ViewChildren, QueryList, ElementRef,
  ChangeDetectorRef, HostListener, ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule, AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject, Subscription, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppState } from '@store/app.state';
import { FrameActions } from '@store/actions/frame.actions';
import {
  selectAllFrames, selectCurrentFrameId, selectLayers,
  selectCels, selectActiveLayerId
} from '@store/selectors/frame.selectors';
import { selectSettings } from '@store/selectors/settings.selector';
import { SettingsActions } from '@store/actions/settings.actions';
import { CollaborationService, PeerState } from '@app/services/collaboration.service';
import { PlaybackService } from '@app/services/playback.service';
import { PixelFrame, celKey } from '@models/frame.model';
import { SpriteLayer } from '@models/layer.model';
import { PxlNumberStepperDirective } from '@app/shared/pxl-number-stepper/pxl-number-stepper.directive';

@Component({
  selector: 'app-frames',
  standalone: true,
  imports: [CommonModule, AsyncPipe, FormsModule, PxlNumberStepperDirective],
  templateUrl: './frames.html',
  styleUrls: ['./frames.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Frames implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  // ── Canvas references ──────────────────────────────────────────────────────
  @ViewChildren('celCanvas')   celCanvases!:   QueryList<ElementRef<HTMLCanvasElement>>;
  @ViewChildren('frameCanvas') frameCanvases!: QueryList<ElementRef<HTMLCanvasElement>>;

  // ── Observables ────────────────────────────────────────────────────────────
  frames$: Observable<PixelFrame[]>;
  layers$: Observable<SpriteLayer[]>;
  currentFrameId$: Observable<string | null>;
  activeLayerId$: Observable<string | null>;
  peers$: Observable<PeerState[]>;

  // ── Local state ────────────────────────────────────────────────────────────
  frames: PixelFrame[]    = [];
  layers: SpriteLayer[]   = [];
  cels:   Record<string, string[][]> = {};
  currentFrameId: string | null = null;
  activeLayerId:  string | null = null;

  isPlaying = false;

  // Layer rename
  editingLayerId: string | null = null;
  editingName = '';

  // Expanded layer props
  expandedLayerId: string | null = null;
  blendModes = ['normal', 'multiply', 'screen', 'overlay'];

  // Onion skin
  onionEnabled = false;
  onionFrames  = 2;
  onionOpacity = 20;

  // Frame drag-and-drop
  dragSourceFrameIdx: number | null = null;
  dragOverFrameIdx:   number | null = null;

  // Context menu
  contextMenuOpen    = false;
  contextMenuX       = 0;
  contextMenuY       = 0;
  contextMenuFrameId: string | null = null;

  // Cel context menu
  celContextMenuOpen   = false;
  celContextMenuX      = 0;
  celContextMenuY      = 0;
  celContextFrameId:   string | null = null;
  celContextLayerId:   string | null = null;

  // Layer panel resize
  layerPanelWidth = 240;
  resizing        = false;
  private resizeStartX = 0;
  private resizeStartW = 0;

  private destroy$ = new Subject<void>();
  private celRenderCache   = new Map<string, string>(); // celKey  → sample-hash
  private frameRenderCache = new Map<string, string>(); // frameId → sample-hash
  private thumbnailsDirty  = false;
  private _sub!: Subscription;
  /** Tracks which layer IDs were present on the previous render cycle.
   *  Used to detect layers that reappear after undo so their stale cache
   *  entries are evicted (otherwise the hash matches and the thumbnail is never repainted). */
  private prevLayerIds = new Set<string>();
  /** Tracks which frame IDs were present on the previous render cycle.
   *  Symmetric with prevLayerIds: when switching projects the Frames component
   *  re-uses existing canvas DOM elements for frame IDs that were last seen in
   *  a different project.  The frameRenderCache would hold the old hash and
   *  skip repainting the now-blank canvas.  Evicting the entry forces a repaint. */
  private prevFrameIds = new Set<string>();

  constructor(
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
    public  collabSvc: CollaborationService,
    private playbackSvc: PlaybackService,
  ) {
    this.frames$         = this.store.select(selectAllFrames);
    this.layers$         = this.store.select(selectLayers);
    this.currentFrameId$ = this.store.select(selectCurrentFrameId);
    this.activeLayerId$  = this.store.select(selectActiveLayerId);
    this.peers$          = this.collabSvc.peers$;
  }

  ngOnInit(): void {
    // NOTE: FrameActions.loadFrames() is intentionally NOT dispatched here.
    //
    // All startup paths in app.ts load project data via restoreFrameState() or
    // setFrames() BEFORE setting showStartup = false (which causes this component
    // to mount).  Calling loadFrames() here would trigger the loadFrames$ effect,
    // which reads from the legacy 'db-store' key — a key that holds a blank frame
    // after a canvas wipe.  That blank frame would then overwrite the correctly
    // restored project data via loadFramesSuccess, producing an empty canvas every
    // time the user opens a project from the recent-projects list.
    //
    // app.ts is the sole authority for initial data loading; this component only
    // needs to subscribe to the store and render what is already there.

    combineLatest([
      this.store.select(selectAllFrames),
      this.store.select(selectLayers),
      this.store.select(selectCels),
      this.store.select(selectCurrentFrameId),
      this.store.select(selectActiveLayerId),
    ]).pipe(takeUntil(this.destroy$)).subscribe(([frames, layers, cels, cfId, alId]) => {
      this.frames         = frames;
      this.layers         = layers;
      this.cels           = cels;
      this.currentFrameId = cfId;
      this.activeLayerId  = alId;

      // Bug-2 fix: when a layer reappears (e.g. restored by collab or solo undo),
      // its celRenderCache entry carries a stale hash that matches the restored pixel
      // data exactly — so renderCelThumbs() would skip repainting the canvas.
      // Detect any layer ID present now but absent last cycle and evict every cache
      // entry for that layer ID, forcing a repaint on the next render pass.
      const currentLayerIds = new Set(layers.map(l => l.id));
      for (const id of currentLayerIds) {
        if (!this.prevLayerIds.has(id)) {
          for (const key of this.celRenderCache.keys()) {
            if (key.endsWith(`|${id}`)) this.celRenderCache.delete(key);
          }
        }
      }
      this.prevLayerIds = currentLayerIds;

      // Thumbnail fix: when a frame ID reappears after a project switch, its
      // frameRenderCache entry holds the hash from the previous visit.  The *ngFor
      // will have created a fresh (blank) canvas element for it, but the hash match
      // would prevent renderFrameThumbs() from repainting.  Evict the entry so the
      // frame thumbnail is always repainted when a frame returns to the visible set.
      const currentFrameIds = new Set(frames.map(f => f.id));
      for (const id of currentFrameIds) {
        if (!this.prevFrameIds.has(id)) {
          this.frameRenderCache.delete(id);
        }
      }
      this.prevFrameIds = currentFrameIds;

      this.thumbnailsDirty = true;
      this.cdr.markForCheck();
      // A second pass after Angular has rendered the new canvases into the DOM
      setTimeout(() => { this.thumbnailsDirty = true; this.renderAllThumbs(); this.cdr.markForCheck(); }, 60);
    });

    this.store.select(selectSettings).pipe(takeUntil(this.destroy$)).subscribe(s => {
      this.onionEnabled = s.onionSkinEnabled;
      this.onionFrames  = s.onionSkinFrames;
      this.onionOpacity = s.onionSkinOpacity;
      if (s.layerPanelWidth && !this.resizing) {
        this.layerPanelWidth = s.layerPanelWidth;
      }
    });

    this.playbackSvc.isPlaying$.pipe(takeUntil(this.destroy$)).subscribe(v => { this.isPlaying = v; });

    this.currentFrameId$.pipe(takeUntil(this.destroy$)).subscribe(id => {
      if (!id) return;
      setTimeout(() => this.scrollActiveFrameIntoView(id, this.isPlaying), 20);
    });
  }

  ngAfterViewInit(): void {
    // The frame/cel canvas elements live inside *ngFor directives driven by the
    // async pipe.  The async pipe may not populate until AFTER the initial
    // change-detection cycle that fires ngOnInit (and the combineLatest callback),
    // so @ViewChildren QueryLists can be empty on the first ngAfterViewChecked.
    //
    // Strategy:
    //   1. Try to render immediately — succeeds when the async pipe emitted during
    //      the initial CD cycle (the common case for NgRx selectors that hold a value).
    //   2. Subscribe to QueryList.changes — fires whenever Angular adds or removes
    //      canvas elements (async-pipe deferred case, or frame/layer count changes).
    //      We use setTimeout(0) to let the current render cycle finish before painting.
    this.thumbnailsDirty = true;
    this.renderAllThumbs();

    const scheduleRender = () => {
      this.thumbnailsDirty = true;
      setTimeout(() => { this.renderAllThumbs(); this.cdr.markForCheck(); }, 0);
    };
    this.celCanvases.changes.pipe(takeUntil(this.destroy$)).subscribe(scheduleRender);
    this.frameCanvases.changes.pipe(takeUntil(this.destroy$)).subscribe(scheduleRender);
  }

  ngAfterViewChecked(): void {
    if (this.thumbnailsDirty) { this.thumbnailsDirty = false; this.renderAllThumbs(); }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._sub?.unsubscribe();
  }

  // ── Thumbnail rendering ────────────────────────────────────────────────────

  /** Render both frame-header previews and cel thumbnails. */
  renderAllThumbs(): void {
    this.renderFrameThumbs();
    this.renderCelThumbs();
  }

  /** Render composited frame pixels into each frame-header canvas. */
  renderFrameThumbs(): void {
    if (!this.frameCanvases) return;
    this.frameCanvases.forEach(ref => {
      const el = ref.nativeElement;
      const frameId = el.dataset['frameId'];
      if (!frameId) return;
      const frame = this.frames.find(f => f.id === frameId);
      if (!frame) return;
      const pixels = frame.pixels;
      const h = pixels.length, w = pixels[0]?.length ?? 0;
      if (!h || !w) return;

      const hash = this.quickPixelHash(pixels);
      if (this.frameRenderCache.get(frameId) === hash) return;
      this.frameRenderCache.set(frameId, hash);

      this.paintCanvasFromPixels(el, pixels);
    });
  }

  /** Render per-layer cel pixel data into each cel canvas. */
  renderCelThumbs(): void {
    if (!this.celCanvases) return;
    this.celCanvases.forEach(ref => {
      const el = ref.nativeElement;
      const frameId = el.dataset['frameId'];
      const layerId = el.dataset['layerId'];
      if (!frameId || !layerId) return;

      const frame = this.frames.find(f => f.id === frameId);
      if (!frame) return;
      const h = frame.pixels.length, w = frame.pixels[0]?.length ?? 0;
      if (!h || !w) return;

      const key    = celKey(frameId, layerId);
      const pixels = this.cels[key];
      const hash   = pixels ? this.quickPixelHash(pixels) : 'empty';
      const cacheKey = `${frameId}|${layerId}`;
      if (this.celRenderCache.get(cacheKey) === hash) return;
      this.celRenderCache.set(cacheKey, hash);

      el.width = w; el.height = h;
      const ctx = el.getContext('2d');
      if (!ctx) return;

      if (!pixels) {
        ctx.clearRect(0, 0, w, h);
        return;
      }
      this.paintCanvasFromPixels(el, pixels);
    });
  }

  /** Paint a pixel grid (string[][] of hex colours) onto a canvas element. */
  private paintCanvasFromPixels(el: HTMLCanvasElement, pixels: string[][]): void {
    const h = pixels.length, w = pixels[0]?.length ?? 0;
    if (!h || !w) return;
    el.width = w; el.height = h;
    const ctx = el.getContext('2d');
    if (!ctx) return;
    const imageData = ctx.createImageData(w, h);
    const d = imageData.data;
    for (let r = 0; r < h; r++) {
      const row = pixels[r];
      for (let c = 0; c < w; c++) {
        const raw = row?.[c] ?? '#00000000';
        const hex = (raw === 'transparent' ? '#00000000' : raw).replace('#', '');
        const idx = (r * w + c) * 4;
        d[idx]   = parseInt(hex.slice(0, 2), 16);
        d[idx+1] = parseInt(hex.slice(2, 4), 16);
        d[idx+2] = parseInt(hex.slice(4, 6), 16);
        d[idx+3] = hex.length >= 8 ? parseInt(hex.slice(6, 8), 16) : 255;
      }
    }
    ctx.putImageData(imageData, 0, 0);
  }

  /**
   * Content hash for change-detection before repainting a thumbnail canvas.
   * Scans every pixel for typical pixel-art sizes (≤128 in each dimension),
   * and falls back to a uniform stride for larger canvases — giving full
   * coverage regardless of where in the image the user drew.
   *
   * The old 11-point sampler caused false cache hits: drawing anywhere except
   * those 11 fixed positions left the hash unchanged and the thumbnail stale.
   */
  private quickPixelHash(pixels: string[][]): string {
    const h = pixels.length, w = pixels[0]?.length ?? 0;
    if (!h || !w) return `${h}x${w}`;
    // Step size: 1 (every pixel) for small canvases; scales up for large ones
    const rStep = Math.max(1, Math.floor(h / 128));
    const cStep = Math.max(1, Math.floor(w / 128));
    let hash = (h * 7919 + w * 6271) | 0;
    for (let r = 0; r < h; r += rStep) {
      const row = pixels[r];
      for (let c = 0; c < w; c += cStep) {
        const px = row?.[c] ?? '#00000000';
        // Mix the 6-char RGB portion into a running integer hash
        const v = parseInt(px.length >= 7 ? px.slice(1, 7) : '000000', 16);
        hash = Math.imul(hash ^ (v + (r << 8) + c), 2654435761) | 0;
      }
    }
    return hash.toString(36);
  }

  // ── Track-by helpers ───────────────────────────────────────────────────────
  trackByFrameId(_: number, f: PixelFrame)  { return f.id; }
  trackByLayerId(_: number, l: SpriteLayer) { return l.id; }
  trackByCelKey(_: number, item: { frameId: string; layerId: string }) {
    return item.frameId + '|' + item.layerId;
  }

  // ── Grid helpers ──────────────────────────────────────────────────────────
  celHasPixels(frameId: string, layerId: string): boolean {
    return !!this.cels[celKey(frameId, layerId)];
  }

  isActiveCel(frameId: string, layerId: string): boolean {
    return frameId === this.currentFrameId && layerId === this.activeLayerId;
  }

  peersOnFrame(peers: PeerState[], frameId: string): PeerState[] {
    return peers.filter(p => p.activeFrameId === frameId);
  }

  // ── Frame actions ─────────────────────────────────────────────────────────
  selectFrame(id: string):   void { this.store.dispatch(FrameActions.selectFrame({ frameId: id })); }
  addBlankFrame():           void {
    if (!this.collabSvc.can('createFrame')) return;
    this.store.dispatch(FrameActions.addBlankFrame());
  }
  deleteFrame(id: string, e: Event): void {
    e.stopPropagation();
    if (!this.collabSvc.can('deleteFrame')) return;
    this.store.dispatch(FrameActions.deleteFrame({ frameId: id }));
  }

  // ── Layer actions ─────────────────────────────────────────────────────────
  addLayer(): void {
    // Bug-4/5 fix: spectators and draw-only members must not create layers.
    if (!this.collabSvc.can('createLayer')) return;
    this.store.dispatch(FrameActions.addLayer());
  }

  selectLayer(l: SpriteLayer): void { this.store.dispatch(FrameActions.selectLayer({ layerId: l.id })); }

  deleteLayer(l: SpriteLayer): void {
    // Bug-3/4/5 fix: spectators and draw-only members must not delete layers.
    // Without this check, the reducer removes the layer locally but the effect
    // skips the broadcast (canBroadcastCanvasSync() is false for spectators),
    // leaving other peers' layer lists out of sync.
    if (!this.collabSvc.can('deleteLayer')) return;
    this.store.dispatch(FrameActions.deleteLayer({ layerId: l.id }));
  }

  toggleVisible(l: SpriteLayer): void { this.store.dispatch(FrameActions.toggleLayerVisible({ layerId: l.id })); }
  toggleLocked(l: SpriteLayer):  void { this.store.dispatch(FrameActions.toggleLayerLocked({ layerId: l.id })); }
  toggleContinuous(l: SpriteLayer): void { this.store.dispatch(FrameActions.toggleLayerContinuous({ layerId: l.id })); }
  // The layer list renders reversed (store index 0 = bottom, last = top visually).
  // Visual ↑ means higher store index → dispatch moveLayerDown (increments idx).
  // Visual ↓ means lower store index  → dispatch moveLayerUp  (decrements idx).
  moveLayerUp(l: SpriteLayer): void {
    if (!this.collabSvc.can('createLayer')) return;
    this.store.dispatch(FrameActions.moveLayerDown({ layerId: l.id }));
  }
  moveLayerDown(l: SpriteLayer): void {
    if (!this.collabSvc.can('createLayer')) return;
    this.store.dispatch(FrameActions.moveLayerUp({ layerId: l.id }));
  }
  mergeDown(l: SpriteLayer): void {
    if (!this.collabSvc.can('deleteLayer')) return;
    this.store.dispatch(FrameActions.mergeLayersDown({ layerId: l.id }));
  }

  setOpacity(l: SpriteLayer, val: string): void {
    this.store.dispatch(FrameActions.setLayerOpacity({ layerId: l.id, opacity: +val }));
  }
  setBlend(l: SpriteLayer, mode: string): void {
    this.store.dispatch(FrameActions.setLayerBlend({ layerId: l.id, blendMode: mode }));
  }
  setTint(l: SpriteLayer, color: string): void {
    this.store.dispatch(FrameActions.setLayerTint({ layerId: l.id, tintColor: color === '#000000' ? null : color }));
  }
  setHold(l: SpriteLayer, val: string): void {
    this.store.dispatch(FrameActions.setLayerHold({ layerId: l.id, holdDuration: +val }));
  }

  // ── Layer rename ──────────────────────────────────────────────────────────
  startRename(l: SpriteLayer): void { this.editingLayerId = l.id; this.editingName = l.name; }
  commitRename(): void {
    if (this.editingLayerId && this.editingName.trim()) {
      this.store.dispatch(FrameActions.renameLayer({ layerId: this.editingLayerId, name: this.editingName.trim() }));
    }
    this.editingLayerId = null;
  }

  // ── Expand / collapse layer properties ───────────────────────────────────
  toggleExpandLayer(l: SpriteLayer): void {
    this.expandedLayerId = this.expandedLayerId === l.id ? null : l.id;
  }

  // ── Cel click – select frame + layer ──────────────────────────────────────
  onCelClick(frameId: string, layerId: string): void {
    this.store.dispatch(FrameActions.selectFrame({ frameId }));
    this.store.dispatch(FrameActions.selectLayer({ layerId }));
  }

  // ── Cel context menu ──────────────────────────────────────────────────────
  onCelContextMenu(e: MouseEvent, frameId: string, layerId: string): void {
    e.preventDefault(); e.stopPropagation();
    this.celContextFrameId  = frameId;
    this.celContextLayerId  = layerId;
    this.celContextMenuX = Math.min(e.clientX, window.innerWidth  - 180 - 8);
    this.celContextMenuY = Math.min(e.clientY, window.innerHeight - 120 - 8);
    this.celContextMenuOpen = true;
    this.contextMenuOpen    = false;
  }

  clearCelFromMenu(): void {
    if (!this.celContextFrameId || !this.celContextLayerId) { this.closeCelContextMenu(); return; }
    this.store.dispatch(FrameActions.clearCel({
      frameId: this.celContextFrameId,
      layerId: this.celContextLayerId,
    }));
    this.closeCelContextMenu();
  }

  closeCelContextMenu(): void { this.celContextMenuOpen = false; this.celContextFrameId = null; this.celContextLayerId = null; }

  // ── Frame context menu ────────────────────────────────────────────────────
  onFrameContextMenu(e: MouseEvent, frameId: string): void {
    e.preventDefault(); e.stopPropagation();
    this.contextMenuFrameId = frameId;
    this.selectFrame(frameId);
    const menuW = 170, menuH = 60;
    this.contextMenuX = Math.min(e.clientX, window.innerWidth  - menuW - 8);
    this.contextMenuY = Math.min(e.clientY, window.innerHeight - menuH - 8);
    this.contextMenuOpen    = true;
    this.celContextMenuOpen = false;
  }

  duplicateFrameFromMenu(e?: Event): void {
    e?.stopPropagation();
    if (!this.contextMenuFrameId || !this.canDuplicateCurrentFrame()) { this.closeContextMenu(); return; }
    this.store.dispatch(FrameActions.duplicateFrame({ frameId: this.contextMenuFrameId }));
    this.closeContextMenu();
  }

  canDuplicateCurrentFrame(): boolean {
    if (!this.collabSvc.can('createFrame')) return false;
    if (this.collabSvc.isInRoom$.value && !this.collabSvc.canBroadcastCanvasSync()) return false;
    return true;
  }

  closeContextMenu(): void { this.contextMenuOpen = false; this.contextMenuFrameId = null; }

  // ── Frame drag-and-drop ───────────────────────────────────────────────────
  onFrameDragStart(e: DragEvent, i: number): void {
    this.dragSourceFrameIdx = i;
    if (e.dataTransfer) { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); }
  }
  onFrameDragOver(e: DragEvent, i: number):  void { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'; this.dragOverFrameIdx = i; }
  onFrameDragLeave():                         void { this.dragOverFrameIdx = null; }
  onFrameDrop(e: DragEvent, to: number):     void {
    e.preventDefault();
    const from = this.dragSourceFrameIdx;
    if (from !== null && from !== to) this.store.dispatch(FrameActions.moveFrame({ fromIndex: from, toIndex: to }));
    this.dragSourceFrameIdx = null; this.dragOverFrameIdx = null;
  }
  onFrameDragEnd(): void { this.dragSourceFrameIdx = null; this.dragOverFrameIdx = null; }

  // ── Layer panel resize ────────────────────────────────────────────────────
  onResizeStart(e: MouseEvent): void {
    e.preventDefault();
    this.resizing      = true;
    this.resizeStartX  = e.clientX;
    this.resizeStartW  = this.layerPanelWidth;
  }

  @HostListener('document:mousemove', ['$event'])
  onDocMouseMove(e: MouseEvent): void {
    if (!this.resizing) return;
    this.layerPanelWidth = Math.max(140, Math.min(520, this.resizeStartW + (e.clientX - this.resizeStartX)));
    this.cdr.markForCheck();
  }

  @HostListener('document:mouseup')
  onDocMouseUp(): void {
    if (!this.resizing) return;
    this.resizing = false;
    this.store.dispatch(SettingsActions.updateSettings({
      settings: { layerPanelWidth: this.layerPanelWidth } as any
    }));
  }

  // ── Scroll helper ─────────────────────────────────────────────────────────
  scrollActiveFrameIntoView(frameId: string, instant = false): void {
    const scrollEl = document.querySelector('.tl-right') as HTMLElement | null;
    if (!scrollEl) return;
    const idx = this.frames.findIndex(f => f.id === frameId);
    if (idx === -1) return;
    const celW    = 48;
    const colLeft  = idx * (celW + 1);
    const colRight = colLeft + celW;
    const sl = scrollEl.scrollLeft;
    const sr = sl + scrollEl.clientWidth;
    if (colLeft < sl)        scrollEl.scrollTo({ left: colLeft  - 8, behavior: instant ? 'instant' as ScrollBehavior : 'smooth' });
    else if (colRight > sr)  scrollEl.scrollTo({ left: colRight - scrollEl.clientWidth + 8, behavior: instant ? 'instant' as ScrollBehavior : 'smooth' });
  }

  // ── Onion skin ────────────────────────────────────────────────────────────
  toggleOnion(): void {
    this.onionEnabled = !this.onionEnabled;
    this.store.dispatch(SettingsActions.updateSettings({ settings: { onionSkinEnabled: this.onionEnabled } as any }));
  }
  updateOnionFrames(v: string): void {
    this.onionFrames = +v;
    this.store.dispatch(SettingsActions.updateSettings({ settings: { onionSkinFrames: this.onionFrames } as any }));
  }
  updateOnionOpacity(v: string): void {
    this.onionOpacity = +v;
    this.store.dispatch(SettingsActions.updateSettings({ settings: { onionSkinOpacity: this.onionOpacity } as any }));
  }

  // ── Document listeners ────────────────────────────────────────────────────
  @HostListener('document:click')
  onDocClick(): void { if (this.contextMenuOpen) this.closeContextMenu(); if (this.celContextMenuOpen) this.closeCelContextMenu(); }

  @HostListener('document:contextmenu')
  onDocCtxMenu(): void { if (this.contextMenuOpen) this.closeContextMenu(); if (this.celContextMenuOpen) this.closeCelContextMenu(); }

  @HostListener('document:keydown.escape')
  onEsc(): void { this.closeContextMenu(); this.closeCelContextMenu(); }
}
